/**
 * Feature flags hook + upgrade-gate component used everywhere the frontend
 * needs to know "can this user use feature X?". One round-trip on mount;
 * results cached in module scope for the page lifetime.
 *
 * Usage:
 *   const { allowed, locked, requiredTiers, currentTier, loading } = useFeature('weekly_themes');
 *   if (locked) return <FeatureLockedCard featureName="Weekly Themes" requiredTiers={requiredTiers} />;
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Module-scope cache so multiple `useFeature` calls on the same page reuse
// the single network request.
let _featuresCache = null;
let _inflightPromise = null;
const _subscribers = new Set();

const broadcast = () => { _subscribers.forEach((cb) => cb()); };

const fetchFeatures = async (api) => {
  if (_featuresCache) return _featuresCache;
  if (_inflightPromise) return _inflightPromise;
  _inflightPromise = api.get('/me/features')
    .then((r) => {
      _featuresCache = r.data;
      broadcast();
      return _featuresCache;
    })
    .catch(() => {
      // Fail-open: if the endpoint errors (e.g. older deploy), let the UI
      // render everything as allowed rather than locking the user out.
      _featuresCache = { tier: 'free', is_admin: false, flags: {}, details: {} };
      return _featuresCache;
    })
    .finally(() => { _inflightPromise = null; });
  return _inflightPromise;
};

export const invalidateFeaturesCache = () => {
  _featuresCache = null;
  broadcast();
};

export const useFeatures = () => {
  const { api } = useAuth();
  const [state, setState] = useState(_featuresCache);

  useEffect(() => {
    let mounted = true;
    const onChange = () => { if (mounted) setState(_featuresCache); };
    _subscribers.add(onChange);
    if (!_featuresCache) {
      fetchFeatures(api).then(() => { if (mounted) setState(_featuresCache); });
    }
    return () => { mounted = false; _subscribers.delete(onChange); };
  }, [api]);

  return {
    loading: !state,
    tier: state?.tier || 'free',
    isAdmin: !!state?.is_admin,
    flags: state?.flags || {},
    details: state?.details || {},
  };
};

export const useFeature = (key) => {
  const { loading, flags, details, tier, isAdmin } = useFeatures();
  const allowed = isAdmin || !!flags[key];
  return {
    loading,
    allowed,
    locked: !loading && !allowed,
    requiredTiers: details[key]?.required_tiers || [],
    featureName: details[key]?.name || key,
    description: details[key]?.description || '',
    currentTier: tier,
    isAdmin,
  };
};

/**
 * Drop-in card to show when a user hits a locked feature in the UI.
 * Used inside page bodies (e.g. WeeklyThemesPage) when `useFeature` reports
 * `locked === true`.
 */
export const FeatureLockedCard = ({
  featureName,
  requiredTiers = [],
  description = '',
  testId = 'feature-locked-card',
}) => {
  const navigate = useNavigate();
  const tiersLabel = requiredTiers.length
    ? requiredTiers.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(', ')
    : 'a higher';
  return (
    <Card className="max-w-xl mx-auto border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid={testId}>
      <CardContent className="py-10 px-6 text-center space-y-4">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-100 text-indigo-600">
          <Lock className="w-7 h-7" />
        </div>
        <div className="space-y-1.5">
          <h2 className="text-xl font-bold text-zinc-900 flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4 text-indigo-600" />
            {featureName} is locked on your plan
          </h2>
          {description && (
            <p className="text-sm text-zinc-600 max-w-md mx-auto">{description}</p>
          )}
          <p className="text-sm text-zinc-500">
            Available on <span className="font-semibold text-indigo-700">{tiersLabel}</span> plan{requiredTiers.length === 1 ? '' : 's'}.
          </p>
        </div>
        <Button
          onClick={() => navigate('/subscription')}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid={`${testId}-upgrade-btn`}
        >
          See plans
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
};
